/**
 * `<SeoHead>` — the SEO boundary that renders the per-route meta-tag
 * bundle into `document.head` via `react-helmet-async` (task 10.2,
 * public-website spec, design §6.2 / Requirement 11.6 / 11.7 / 11.8).
 *
 * The component takes the same input shape as `buildMeta` (task
 * 10.1) and calls `buildMeta` internally. Page containers therefore
 * never need to import `buildMeta` separately — they construct a
 * `MetaInput` from their route's data (per-record API response or
 * brand-level `BrandingConfig.share.*`) and hand it to `<SeoHead>`.
 *
 * Renders inside `<HelmetProvider>` (wired in `src/App.tsx`):
 *
 * - `<title>{tags.title}</title>`
 * - `<meta name="description" content={tags.description} />`
 * - `<link rel="canonical" href={tags.canonical} />`
 * - one `<meta property={...} content={...} />` per OpenGraph entry
 * - one `<meta name={...} content={...} />` per Twitter-card entry
 * - one `<script type="application/ld+json">…</script>` per
 *   resolved JSON-LD blob (omitted entirely when `jsonLdType` is not
 *   supplied to the builder).
 *
 * Graceful TODO-027 / TODO-028 / TODO-029 omission is handled
 * upstream by `buildMeta`: undefined branding fields drop their
 * corresponding tag from the array rather than emit empty
 * `content=""` strings, so the renderer here is free to map straight
 * across the bundle without conditional logic per field.
 *
 * Validates: Requirements 11.6, 11.7, 11.8 (renderer side).
 */

import { Helmet } from 'react-helmet-async';
import { buildMeta, type MetaInput } from '../lib/seo/meta-builder';

/**
 * Render the per-route meta-tag bundle into `document.head`.
 *
 * @param props The same input shape as `buildMeta`. The component
 *   resolves the bundle internally and renders it via `<Helmet>`.
 * @returns A `<Helmet>` element. Page containers mount this once
 *   per route, typically as the first child of the page body.
 */
export function SeoHead(props: MetaInput): JSX.Element {
  const tags = buildMeta(props);
  return (
    <Helmet>
      <title>{tags.title}</title>
      <meta name="description" content={tags.description} />
      <link rel="canonical" href={tags.canonical} />
      {tags.openGraph.map((tag) => (
        <meta key={`og:${tag.property}`} property={tag.property} content={tag.content} />
      ))}
      {tags.twitterCard.map((tag) => (
        <meta key={`tw:${tag.name}`} name={tag.name} content={tag.content} />
      ))}
      {tags.jsonLd !== undefined ? (
        <script type="application/ld+json">{JSON.stringify(tags.jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}

export default SeoHead;
